

import api from "../lib/api";
import { handleError } from "../lib/errors";
import { LIMITS } from "../lib/constants";


export interface Participante {
  id: string;
  nome: string;
  empresa?: string;
  cargo?: string;
  email?: string;
  ata_id: string;
}


interface BackendParticipante {
  id: number;
  nome: string;
  empresa?: string;
  cargo?: string;
  email?: string;
  ata_id: number;
}


function mapBackendToFrontend(backend: BackendParticipante): Participante {
  return {
    id: backend.id.toString(),
    nome: backend.nome,
    empresa: backend.empresa,
    cargo: backend.cargo,
    email: backend.email,
    ata_id: backend.ata_id.toString(),
  };
}


export async function getParticipantesByAtaId(
  ataId: string
): Promise<Participante[]> {
  try {
    const numericId = Number(ataId);
    if (isNaN(numericId)) {
      throw new Error("ID da ata inválido");
    }

    const response = await api.get(`/participantes/${numericId}`);
    const backendParticipantes = Array.isArray(response.data)
      ? response.data
      : [];
    return backendParticipantes.map(mapBackendToFrontend);
  } catch (error) {
    const message = handleError(error, "getParticipantesByAtaId");
    throw new Error(message);
  }
}


export async function createParticipante(
  ataId: string,
  input: {
    nome: string;
    empresa?: string;
    cargo?: string;
    email?: string;
  }
): Promise<Participante> {
  try {
    const numericId = Number(ataId);
    if (isNaN(numericId)) {
      throw new Error("ID da ata inválido");
    }

    // Validações
    if (!input.nome || !input.nome.trim()) {
      throw new Error("Nome do participante é obrigatório.");
    }
    if (input.nome.trim().length > LIMITS.PARTICIPANT_NAME_MAX) {
      throw new Error(`Nome excedeu o limite de ${LIMITS.PARTICIPANT_NAME_MAX} caracteres.`);
    }
    if (input.empresa && input.empresa.length > LIMITS.PARTICIPANT_COMPANY_MAX) {
      throw new Error(`Empresa excedeu o limite de ${LIMITS.PARTICIPANT_COMPANY_MAX} caracteres.`);
    }
    if (input.cargo && input.cargo.length > LIMITS.PARTICIPANT_ROLE_MAX) {
      throw new Error(`Cargo excedeu o limite de ${LIMITS.PARTICIPANT_ROLE_MAX} caracteres.`);
    }

    const response = await api.post(`/participantes/${numericId}`, input);

    if (!response.data || !response.data.id) {
      throw new Error("Resposta inválida do servidor ao criar participante");
    }

    return mapBackendToFrontend(response.data);
  } catch (error) {
    const message = handleError(error, "createParticipante");
    throw new Error(message);
  }
}


export async function updateParticipante(
  participanteId: string,
  input: {
    nome?: string;
    empresa?: string;
    cargo?: string;
    email?: string;
  }
): Promise<Participante> {
  try {
    const numericId = Number(participanteId);
    if (isNaN(numericId)) {
      throw new Error("ID do participante inválido");
    }

    const response = await api.put(`/participantes/${numericId}`, input);

    if (!response.data || !response.data.id) {
      throw new Error(
        "Resposta inválida do servidor ao atualizar participante"
      );
    }

    return mapBackendToFrontend(response.data);
  } catch (error) {
    const message = handleError(error, "updateParticipante");
    throw new Error(message);
  }
}


export async function deleteParticipante(
  participanteId: string
): Promise<void> {
  try {
    const numericId = Number(participanteId);
    if (isNaN(numericId)) {
      throw new Error("ID do participante inválido");
    }

    await api.delete(`/participantes/${numericId}`);
  } catch (error) {
    const message = handleError(error, "deleteParticipante");
    throw new Error(message);
  }
}


export async function obterHistoricoParticipantes(projectId?: string): Promise<string[]> {
  try {
    const url = projectId
      ? `/participantes/historico/recentes?projectId=${encodeURIComponent(projectId)}`
      : "/participantes/historico/recentes";
    const response = await api.get<{ participantes: string[] }>(url);

    if (!response.data || !Array.isArray(response.data.participantes)) {
      console.warn('[Frontend] ⚠️ Resposta inválida, retornando array vazio');
      return [];
    }

    return response.data.participantes;
  } catch (error) {
    const message = handleError(error, "obterHistoricoParticipantes");
    console.error('[Frontend] ❌ Erro ao buscar histórico:', message);

    return [];
  }
}


/**
 * Colaborador do projeto (vindo da tabela shares)
 */
export interface ProjectCollaborator {
  user_id: number | null;
  email: string;
  nome: string | null;
  empresa: string | null;
  permission: 'view' | 'edit' | 'owner';
}

/**
 * Busca colaboradores aceitos de um projeto
 */
export async function getProjectCollaborators(
  projectId: string
): Promise<ProjectCollaborator[]> {
  try {
    const numericId = Number(projectId);
    if (isNaN(numericId)) {
      throw new Error("ID do projeto inválido");
    }

    const response = await api.get<{ colaboradores: ProjectCollaborator[] }>(
      `/participantes/colaboradores/${numericId}`
    );

    if (!response.data || !Array.isArray(response.data.colaboradores)) {
      return [];
    }

    return response.data.colaboradores;
  } catch (error) {
    const message = handleError(error, "getProjectCollaborators");
    console.error('[Frontend] ❌ Erro ao buscar colaboradores:', message);
    return [];
  }
}
